import React from 'react';
import PropTypes from 'prop-types';

import './emails.css';

// This is a plain monospace <textarea>, deliberately. It is not a WYSIWYG
// and must never become one.
//
// The approved design draws a rich-text editor in this slot. That design was
// abandoned on 2026-09-01: measuring all 78 live templates showed their copy
// lives inside <td> table cells, so the header/content/footer split the rich
// editor depended on cannot work. A cell-level editing model replaces it and is
// not built yet, so this keeps the control that exists and takes only the
// design's editor frame around it — the caption bar, the hairline border, the
// inset ground.
//
// The templates it edits are complete HTML documents - <!DOCTYPE>, <head>,
// @font-face rules, 4-45 nested tables, and MSO conditional comments
// (<!--[if mso]-->). This repo already has a rich editor, RedactorEditor.jsx
// (src/components/Views/DragAndDrop/RedactorEditor.jsx, wrapping
// src/static/redactor.js), and it would DESTROY these templates:
//
//   - The vendored Redactor 3.4.12 cleaner hardcodes
//     `this.deniedTags = ['font', 'html', 'head', 'link', 'title', 'body', 'meta', 'applet']`
//     (src/static/redactor.js) and strips them out of any content it loads.
//     Loading a template through Redactor would drop the whole <head>,
//     orphaning every @font-face rule.
//   - It defaults `removeComments: true`, which deletes every MSO
//     conditional comment - the very thing that makes these emails render
//     correctly in Outlook.
//   - Independently of both of the above, Redactor sets `innerHTML` on a
//     contenteditable <div>. A <div> cannot hold <!DOCTYPE>, <html> or
//     <head> content at all, even before the cleaner runs.
//
// If someone is tempted to "upgrade" this to Redactor (or any rich editor
// that sanitizes/reparents HTML) because "we have a rich editor right
// there" - don't. It will silently corrupt every template on the next load.
//
// wrap="off" keeps long table rows from wrapping and shifting content under
// the cursor while editing; spellCheck is off because HTML is not prose.
//
// The caption row and the textarea are both inside the <label> on purpose:
// jsx-a11y/label-has-for needs each label to BOTH wrap its control and carry
// htmlFor, and three real accessibility defects in this project came from
// getting exactly that wrong.
const HtmlSourceEditor = ({
  value, onChange, id, label, rows,
}) => (
  <label className="sourcelabel" htmlFor={id}>
    <span className="srcaption">
      {label}
      <span className="count">
        {`${value.length.toLocaleString()} character${value.length === 1 ? '' : 's'}`}
      </span>
    </span>
    <textarea
      id={id}
      className="source-textarea"
      value={value}
      onChange={onChange}
      rows={rows}
      spellCheck={false}
      wrap="off"
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
    />
  </label>
);

HtmlSourceEditor.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  rows: PropTypes.number,
};

HtmlSourceEditor.defaultProps = {
  rows: 24,
};

export default HtmlSourceEditor;
